import { useId } from 'react';
import classNames from 'classnames';
import {
  MONEY_INPUT_MAX_DECIMALS,
  normalizePastedMoney,
  sanitizeMoneyInput,
  settleMoneyInput,
} from './MoneyInput.functions';
import type { tMoneyInputProps } from './MoneyInput.types';
import './MoneyInput.styles.scss';

/**
 * Text field for a money amount, with the currency shown as an attached
 * prefix box.
 *
 * Why `type="text"` with `inputMode="decimal"` rather than `type="number"`:
 * number inputs let the mouse wheel silently change the value while
 * scrolling, accept `e`/`+`/`-` as valid characters, and report an empty
 * string for anything the browser considers invalid — so the caller cannot
 * tell "nothing typed" from "typed something odd". On a money field that
 * matters. `inputMode="decimal"` still brings up the numeric keypad on
 * mobile.
 *
 * The component never parses, rounds or formats: it hands back exactly what
 * was typed. Whoever owns the flow decides what is valid and what it means.
 */
export default function MoneyInput(props: tMoneyInputProps) {
  const generatedId = useId();
  const {
    value,
    onChange,
    currencySymbol,
    label,
    placeholder = '0.00',
    error,
    helper,
    currencyCode,
    filterInput = true,
    disabled = false,
    id,
    ariaLabel,
    ariaDescribedBy,
    className,
    maxDecimals = MONEY_INPUT_MAX_DECIMALS,
  } = props;

  const inputId = id ?? `money-input-${generatedId}`;
  const errorId = error ? `${inputId}-error` : undefined;
  const helperId = !error && helper ? `${inputId}-helper` : undefined;
  const describedBy =
    [errorId, helperId, ariaDescribedBy].filter(Boolean).join(' ') || undefined;

  return (
    <div className={classNames('MoneyInput', className)}>
      {label ? (
        <label className="MoneyInput__Label" htmlFor={inputId}>
          {label}
        </label>
      ) : null}

      <div
        className={classNames('MoneyInput__Field', {
          'MoneyInput__Field--invalid': Boolean(error),
          'MoneyInput__Field--disabled': disabled,
        })}
      >
        {/* aria-hidden: the symbol is decoration here — the accessible name
              carries the currency, so a screen reader should not read "dollar
              sign" as part of the value. */}
        <span className="MoneyInput__Prefix" aria-hidden="true">
          {currencySymbol}
        </span>
        <input
          id={inputId}
          type="text"
          // `numeric` cuando el campo no admite centavos: su teclado móvil no
          // trae el separador decimal, así que la regla se comunica antes de
          // que el cliente teclee algo que el filtro va a descartar. Con
          // decimales permitidos sigue siendo `decimal`, que sí lo trae.
          inputMode={maxDecimals === 0 ? 'numeric' : 'decimal'}
          autoComplete="off"
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          // Filtered before it reaches the caller: inputMode only swaps the
          // mobile keyboard, it does not stop letters or a pasted `1e9`.
          onChange={(event) =>
            onChange(
              filterInput
                ? sanitizeMoneyInput(event.target.value, maxDecimals)
                : event.target.value,
            )
          }
          // Paste is handled apart from typing: pasted text carries
          // thousands separators, and reading "1,234.56" as a decimal comma
          // would turn it into 1.23 — a thousand times less.
          onPaste={(event) => {
            if (!filterInput) return;
            event.preventDefault();
            onChange(
              normalizePastedMoney(
                event.clipboardData.getData('text'),
                maxDecimals,
              ),
            );
          }}
          // Settles a dangling separator once typing is actually done. Only
          // ever fires for `maxDecimals === 0` (see settleMoneyInput's
          // docblock) — a field that takes real decimals is untouched, since
          // "12." mid-entry there is not this component's call.
          onBlur={() => {
            if (!filterInput) return;
            const settled = settleMoneyInput(value, maxDecimals);
            if (settled !== value) onChange(settled);
          }}
          data-testid={props['data-testid']}
        />
        {currencyCode ? (
          <span className="MoneyInput__Suffix" aria-hidden="true">
            {currencyCode}
          </span>
        ) : null}
      </div>

      {error ? (
        <p className="MoneyInput__Error" id={errorId} role="alert">
          {error}
        </p>
      ) : helperId ? (
        <p className="MoneyInput__Helper" id={helperId}>
          {helper}
        </p>
      ) : null}
    </div>
  );
}
